import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
  amountInWords,
  calcRoom,
  formatMoney,
  formatQuantity,
  layoutRoom,
  RENDER_STYLES,
  toDecimal,
  type MeasureRoom,
} from '@erp/shared';
import { estimatesApi, type EstimateLine } from '../api/estimates';
import { rendersApi, useRenderImage, type RoomRender } from '../api/renders';
import { RoomPlan, WallUnfold } from '../components/RoomDrawings';

/**
 * Обмерный план в коммерческом предложении: заказчик видит схему каждого
 * помещения и цифры, из которых получились объёмы в строках сметы, — это
 * снимает вопросы «откуда 31 м² стен» ещё до звонка.
 */
const STYLE_LABELS = Object.fromEntries(RENDER_STYLES.map((s) => [s.code, s.label])) as Record<string, string>;

/**
 * Визуализация в бланке. Сообщает о загрузке (или ошибке) — печать и PDF
 * ждут все картинки, иначе в документ попадёт пустая рамка.
 */
function ProposalImage({ render, onSettled }: { render: RoomRender; onSettled: (id: string) => void }) {
  const { url, isError } = useRenderImage(render.id, 'result', render.imageVersion);
  useEffect(() => {
    if (isError) onSettled(render.id);
  }, [isError, render.id, onSettled]);
  // Только стиль: пожелания — это задание для нейросети («не перекрывай
  // проходы», «убери провода»), заказчику в КП его видеть незачем.
  const caption = render.style ? STYLE_LABELS[render.style] : null;
  return (
    <figure className="plan-render">
      {url && <img src={url} alt="" onLoad={() => onSettled(render.id)} onError={() => onSettled(render.id)} />}
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

function MeasurementsSection({
  rooms,
  renders,
  onImageSettled,
}: {
  rooms: MeasureRoom[];
  renders: RoomRender[];
  onImageSettled: (id: string) => void;
}) {
  return (
    <div className="plans">
      <div className="plans-title">Обмерный план</div>
      {rooms.map((room) => {
        const m = calcRoom(room);
        const layout = layoutRoom(room);
        return (
          <div className="plan-room" key={room.id}>
            <div className="plan-room-head">
              <span className="plan-room-name">{room.name}</span>
              <span className="plan-room-meta">
                пол {formatQuantity(m.FLOOR_AREA)} м² · стены {formatQuantity(m.WALL_AREA)} м² · периметр{' '}
                {formatQuantity(m.PERIMETER)} м · плинтус {formatQuantity(m.BASEBOARD_LENGTH)} м · откосы{' '}
                {formatQuantity(m.SLOPES_LENGTH)} м
              </span>
            </div>
            {layout && (
              <div className="plan-room-body">
                <div className="plan-room-plan">
                  <RoomPlan room={room} layout={layout} />
                </div>
                <div className="plan-room-unfold">
                  <WallUnfold room={room} layout={layout} />
                </div>
              </div>
            )}
            {renders.some((r) => r.roomId === room.id) && (
              <div className="plan-renders">
                {renders
                  .filter((r) => r.roomId === room.id)
                  .map((r) => (
                    <ProposalImage key={r.id} render={r} onSettled={onImageSettled} />
                  ))}
              </div>
            )}
          </div>
        );
      })}
      <div className="plans-note">
        Размеры внутренние, в метрах. Номера стен в кружках — по часовой стрелке; развёртка — вид стены изнутри
        помещения. Площадь стен указана за вычетом окон и дверей.
        {renders.length > 0 &&
          ' Визуализации иллюстрируют стиль отделки и мебель; мебель и декор в стоимость сметы не входят, если не указаны в ней отдельно.'}
      </div>
    </div>
  );
}

/** «08 августа 2026 г.» — формат даты в шапке бланка. */
function formatDateLong(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDateShort(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Реквизиты стороны одной строкой: наименование, ИНН/КПП, адрес — так их
 * печатает 1С в строках «Исполнитель:» и «Заказчик:».
 */
function partyLine(party: {
  fullName: string;
  inn: string | null;
  kpp: string | null;
  addressFull: string | null;
}): string {
  const parts: string[] = [party.fullName];
  if (party.inn) parts.push(party.kpp ? `ИНН ${party.inn}, КПП ${party.kpp}` : `ИНН ${party.inn}`);
  if (party.addressFull) parts.push(party.addressFull);
  return parts.join(', ');
}

interface SectionGroup {
  name: string | null;
  lines: EstimateLine[];
  total: string;
}

/**
 * Группирует строки сметы по разделу (sectionName) в порядке первого
 * появления раздела — сметы печатаются главами с подытогом по каждой,
 * а не плоским списком.
 */
function groupBySection(lines: EstimateLine[]): SectionGroup[] {
  const groups: SectionGroup[] = [];
  const indexByName = new Map<string, number>();

  for (const line of lines) {
    const key = line.sectionName ?? '';
    let idx = indexByName.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByName.set(key, idx);
      groups.push({ name: line.sectionName, lines: [], total: '0' });
    }
    groups[idx]!.lines.push(line);
  }

  for (const group of groups) {
    const sum = group.lines.reduce((acc, l) => acc + Number(l.totalAmount), 0);
    group.total = sum.toFixed(2);
  }

  return groups;
}

/**
 * PDF собирается как «скриншот» отрисованного бланка (html2canvas → jsPDF) —
 * тем же способом, что у акта: печатная форма на экране и в PDF гарантированно
 * совпадают, без второй реализации вёрстки.
 */
async function downloadEstimatePdf(element: HTMLElement, filename: string): Promise<void> {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    ignoreElements: (el) => el.classList.contains('no-print'),
  });

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgHeight = (canvas.height * pageWidth) / canvas.width;
  const imgData = canvas.toDataURL('image/png');

  let heightLeft = imgHeight;
  let position = 0;
  pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position -= pageHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(filename);
}

/**
 * Печатная форма сметы (коммерческого предложения).
 *
 * Стиль сознательно отличается от счёта/акта: те — строгие бухгалтерские
 * бланки (чёрные рамки таблицы, реквизиты банка, QR на оплату, подписи
 * директора и бухгалтера), а смета — предложение стоимости для клиента,
 * без банковских реквизитов и без раздела подписей. Акцентная цветная
 * плашка в шапке и лёгкая таблица без сплошных рамок визуально сигналят:
 * это не документ на оплату, а расчёт для согласования.
 *
 * Строки группируются по разделу с подзаголовком и подытогом — как в
 * сметных программах (Prorab и т.п.), в отличие от плоской таблицы счёта.
 *
 * Отдельный маршрут без AppShell, как у остальных документов: бланк должен
 * занимать весь лист. window.print() вызывается автоматически при обычном
 * открытии.
 */
export function EstimatePrintPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const autoDownload = searchParams.get('download') === '1';
  const [downloading, setDownloading] = useState(false);
  // Схемы можно убрать из бланка: некоторым заказчикам нужна только цена.
  const [showPlans, setShowPlans] = useState(searchParams.get('plans') !== '0');
  const printRef = useRef<HTMLDivElement>(null);

  const estimateQuery = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => estimatesApi.get(id!),
    enabled: !!id,
  });

  const estimate = estimateQuery.data?.estimate;

  const rendersQuery = useQuery({
    queryKey: ['renders', id, 'proposal'],
    queryFn: () => rendersApi.list(id!),
    enabled: !!id,
  });
  const roomIds = new Set((estimate?.measurements ?? []).map((r) => r.id));
  const proposalRenders = (rendersQuery.data?.items ?? []).filter(
    (r) => r.inProposal && r.status === 'DONE' && roomIds.has(r.roomId),
  );
  const [settledImages, setSettledImages] = useState<ReadonlySet<string>>(new Set());
  const onImageSettled = useCallback(
    (renderId: string) => setSettledImages((prev) => (prev.has(renderId) ? prev : new Set(prev).add(renderId))),
    [],
  );
  // Без визуализаций (или со скрытыми схемами) ждать нечего; ошибка списка
  // не должна блокировать печать сметы.
  const imagesReady =
    !showPlans ||
    rendersQuery.isError ||
    (rendersQuery.isFetched && proposalRenders.every((r) => settledImages.has(r.id)));
  const ready = !!estimate && imagesReady;

  function pdfFilename(): string {
    return `Смета № ${estimate!.number} от ${formatDateShort(estimate!.date)}.pdf`;
  }

  async function handleDownload() {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      await downloadEstimatePdf(printRef.current, pdfFilename());
    } finally {
      setDownloading(false);
    }
  }

  // Автопечать — один раз, когда бланк готов целиком. Флажок «Схемы помещений»
  // меняет готовность, но повторно открывать диалог печати не должен.
  const autoPrinted = useRef(false);
  useEffect(() => {
    if (!ready || autoPrinted.current) return;
    const timer = setTimeout(() => {
      autoPrinted.current = true;
      if (autoDownload) void handleDownload();
      else window.print();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, autoDownload]);

  if (!estimate) {
    return <div style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>Загрузка…</div>;
  }

  const org = estimate.organization;
  const cp = estimate.counterparty;

  const vatExempt = estimate.lines.every((l) => l.vatRateValue === null);
  const hasDiscount = !toDecimal(estimate.discountAmount).isZero();
  const groups = groupBySection(estimate.lines);
  const rooms = estimate.measurements ?? [];

  return (
    <div className="estimate-print" ref={printRef}>
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        .estimate-print { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #1f2328; line-height: 1.35; }
        .estimate-print .estimate-page {
          box-sizing: border-box;
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto;
          background: #fff;
        }

        /* Тёмная компактная шапка вместо чёрной линии счёта — сразу видно,
           что это не документ на оплату, а предложение для клиента.
           Радиальное свечение по краям вместо плоской заливки — фактурный
           фон без фото (в системе нет фотобанка для бланков). */
        .estimate-print .head-band {
          position: relative;
          background-color: #17171a;
          background-image:
            radial-gradient(ellipse at 15% 20%, rgba(201,168,105,0.10), transparent 55%),
            radial-gradient(ellipse at 100% 100%, rgba(0,0,0,0.35), transparent 60%);
          color: #f3efe8;
          padding: 8mm 10mm;
          min-height: 32mm;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
        }
        .estimate-print .head-kicker {
          font-size: 9px; letter-spacing: 2px; text-transform: uppercase;
          color: #c9a869; margin-bottom: 5px; font-weight: 700;
        }
        .estimate-print .head-title { font-size: 22px; font-weight: 700; letter-spacing: -0.2px; }
        .estimate-print .head-meta { font-size: 10.5px; color: #a8a29a; margin-top: 4px; }

        .estimate-print .head-row { display: flex; align-items: center; justify-content: space-between; gap: 12mm; }
        /* Логотип обрезан по кругу: файлы логотипов почти всегда квадратные и
           с непрозрачным фоном, а его углы на тёмной шапке читаются лишним
           прямоугольником. Круглая маска убирает углы, и знак ложится на
           шапку без видимых границ. */
        .estimate-print .head-logo {
          flex: 0 0 auto;
          width: 20mm; height: 20mm;
          border-radius: 50%;
          overflow: hidden;
          display: flex; align-items: center; justify-content: center;
        }
        /* Скругление продублировано на самом изображении: html2canvas при сборке
           PDF не всегда применяет overflow-маску родителя. */
        .estimate-print .head-logo img {
          width: 100%; height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }
        /* Тонкая золотая линия по нижнему краю шапки — премиальный «кант»
           вместо толстого цветного бордюра. */
        .estimate-print .head-band::after {
          content: '';
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #c9a869 20%, #c9a869 80%, transparent);
        }

        .estimate-print .body { padding: 8mm 10mm 12mm; }

        .estimate-print .party { display: flex; margin-bottom: 8px; }
        .estimate-print .party .lbl { flex: 0 0 110px; color: #57606a; }
        .estimate-print .party .val { flex: 1 1 auto; font-weight: 600; }

        /* Лёгкая таблица без сплошных чёрных рамок: только горизонтальные
           разделители строк и акцентная линия под шапкой. */
        .estimate-print .items { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 14px; }
        .estimate-print .items thead th {
          border-bottom: 2px solid #17171a;
          padding: 6px 5px;
          font-weight: 700;
          text-align: center;
          color: #17171a;
        }
        .estimate-print .items td {
          border-bottom: 1px solid #e1e4e8;
          padding: 5px;
          vertical-align: top;
          overflow-wrap: anywhere;
        }
        .estimate-print .items td.c { text-align: center; }
        .estimate-print .items td.r { text-align: right; white-space: nowrap; }

        /* Подзаголовок раздела — акцентный текст без заливки, слева. */
        .estimate-print .items td.section-head {
          font-weight: 700;
          color: #17171a;
          text-align: left;
          border-bottom: 1px solid #c9a869;
          padding-top: 12px;
        }
        .estimate-print .items td.section-total {
          font-weight: 700;
          text-align: right;
          color: #57606a;
          border-bottom: 1px dashed #c9d1d9;
        }

        .estimate-print .totals { margin-top: 10px; text-align: right; }
        .estimate-print .totals .row { margin-top: 3px; color: #57606a; }
        .estimate-print .totals .val { display: inline-block; min-width: 90px; text-align: right; }
        .estimate-print .totals .grand { font-size: 13px; font-weight: 700; color: #17171a; margin-top: 6px; }

        .estimate-print .summary { margin-top: 10px; }
        .estimate-print .words { font-weight: 600; }

        .estimate-print .clause {
          margin-top: 16px; padding: 8px 10px;
          background: #f6f8fa; border: 1px solid #e1e4e8;
          font-size: 10.5px; color: #444;
        }

        .estimate-print .plans { margin-top: 18px; }
        .estimate-print .plans-title {
          font-size: 13px; font-weight: 700; color: #17171a;
          border-bottom: 2px solid #17171a; padding-bottom: 4px;
        }
        /* Помещение не разрывается между листами при печати — план без
           половины стен заказчику бесполезен. */
        .estimate-print .plan-room {
          break-inside: avoid; page-break-inside: avoid;
          padding: 10px 0; border-bottom: 1px solid #e1e4e8;
        }
        .estimate-print .plan-room-head { display: flex; flex-wrap: wrap; gap: 4px 12px; align-items: baseline; }
        .estimate-print .plan-room-name { font-weight: 700; font-size: 12px; color: #17171a; }
        .estimate-print .plan-room-meta { color: #57606a; }
        .estimate-print .plan-room-body { display: flex; gap: 14px; align-items: flex-start; margin-top: 8px; }
        .estimate-print .plan-room-plan { flex: 0 0 72mm; }
        .estimate-print .plan-room-unfold { flex: 1 1 auto; min-width: 0; }
        .estimate-print .plan-renders {
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-top: 10px;
        }
        .estimate-print .plan-render { margin: 0; }
        .estimate-print .plan-render img { display: block; width: 100%; height: auto; }
        .estimate-print .plan-render figcaption { margin-top: 3px; font-size: 9.5px; color: #57606a; }
        .estimate-print .plans-note { margin-top: 8px; font-size: 9.5px; color: #8b949e; }

        .estimate-print .disclaimer {
          margin-top: 18px; font-size: 9.5px; color: #8b949e; text-align: center;
        }

        @media screen {
          .estimate-print { background: #ddd; padding: 16px 0; }
          .estimate-print .estimate-page { box-shadow: 0 0 8px rgba(0,0,0,0.2); }
        }
        @media print {
          .no-print { display: none !important; }
          .estimate-print { background: #fff; padding: 0; }
          .estimate-print .estimate-page { min-height: 0; margin: 0; box-shadow: none; }
        }
      `}</style>

      <div
        className="no-print"
        style={{ textAlign: 'center', marginBottom: 12, display: 'flex', gap: 8, justifyContent: 'center' }}
      >
        <button onClick={() => window.print()} style={{ padding: '6px 14px', cursor: 'pointer' }}>
          Печать
        </button>
        <button
          onClick={() => void handleDownload()}
          disabled={downloading}
          style={{ padding: '6px 14px', cursor: 'pointer' }}
        >
          {downloading ? 'Формирование PDF…' : 'Скачать PDF'}
        </button>
        {rooms.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
            <input type="checkbox" checked={showPlans} onChange={(e) => setShowPlans(e.currentTarget.checked)} />
            Схемы помещений
          </label>
        )}
      </div>

      <div className="estimate-page">
        <div className="head-band">
          <div className="head-row">
            <div>
              <div className="head-kicker">Коммерческое предложение</div>
              <div className="head-title">Смета № {estimate.number} от {formatDateLong(estimate.date)}</div>
              {estimate.validUntil && (
                <div className="head-meta">Действительна до {formatDateShort(estimate.validUntil)}</div>
              )}
            </div>
            {org.logoDataUrl && (
              <div className="head-logo">
                <img src={org.logoDataUrl} alt="" />
              </div>
            )}
          </div>
        </div>

        <div className="body">
          <div className="party">
            <span className="lbl">Исполнитель:</span>
            <span className="val">{partyLine(org)}</span>
          </div>
          <div className="party">
            <span className="lbl">Заказчик:</span>
            <span className="val">{partyLine(cp)}</span>
          </div>
          {estimate.objectName && (
            <div className="party">
              <span className="lbl">Объект:</span>
              <span className="val">
                {estimate.objectName}
                {estimate.objectAddress ? `, ${estimate.objectAddress}` : ''}
              </span>
            </div>
          )}

          <table className="items">
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: '48%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>№</th>
                <th>Наименование работ, услуг</th>
                <th>Объём</th>
                <th>Ед.</th>
                <th>Цена</th>
                <th>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group, gi) => (
                <>
                  {group.name && (
                    <tr key={`${gi}-head`}>
                      <td className="section-head" colSpan={6}>
                        {group.name}
                      </td>
                    </tr>
                  )}
                  {group.lines.map((line) => (
                    <tr key={line.id}>
                      <td className="c">{line.lineNumber}</td>
                      <td>{line.name}</td>
                      <td className="c">{formatQuantity(line.quantity)}</td>
                      <td className="c">{line.unitSymbol || '—'}</td>
                      <td className="r">{formatMoney(line.price)}</td>
                      {/* В графе «Сумма» стоит стоимость с НДС: итог таблицы
                          должен сходиться со строкой «Итого». */}
                      <td className="r">{formatMoney(line.totalAmount)}</td>
                    </tr>
                  ))}
                  {group.name && group.lines.length > 1 && (
                    <tr key={`${gi}-total`}>
                      <td colSpan={5} className="section-total">
                        Итого по разделу «{group.name}»:
                      </td>
                      <td className="section-total">{formatMoney(group.total)}</td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>

          <div className="totals">
            {/* Строки таблицы — по прайсовым ценам, скидка — отдельной строкой;
                итоги сметы в базе уже со скидкой. */}
            {hasDiscount && (
              <>
                <div className="row">
                  Итого без скидки:{' '}
                  <span className="val">{formatMoney(toDecimal(estimate.total).plus(estimate.discountAmount))}</span>
                </div>
                <div className="row">
                  Скидка {formatQuantity(estimate.discountPercent!)} %:{' '}
                  <span className="val">−{formatMoney(estimate.discountAmount)}</span>
                </div>
              </>
            )}
            <div className="row">
              Сумма без НДС: <span className="val">{formatMoney(estimate.subtotal)}</span>
            </div>
            <div className="row">
              В том числе НДС:{' '}
              <span className="val">{vatExempt ? 'Без налога (НДС)' : formatMoney(estimate.vatAmount)}</span>
            </div>
            <div className="row grand">
              {hasDiscount ? 'Итого со скидкой' : 'Итого'}: <span className="val">{formatMoney(estimate.total)}</span>
            </div>
          </div>

          <div className="summary">
            <div className="words">{amountInWords(toDecimal(estimate.total))}</div>
          </div>

          {estimate.footerText && <div className="clause">{estimate.footerText}</div>}

          {estimate.comment && <div style={{ marginTop: 12, whiteSpace: 'pre-line' }}>{estimate.comment}</div>}

          {showPlans && rooms.length > 0 && (
            <MeasurementsSection rooms={rooms} renders={proposalRenders} onImageSettled={onImageSettled} />
          )}

          <div className="disclaimer">
            Документ является коммерческим предложением и не признаётся счётом на оплату,
            актом или иным первичным учётным документом.
          </div>
        </div>
      </div>
    </div>
  );
}
